import {
    CircularProgress,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Field, Form, Formik } from 'formik';
import { Link, Navigate } from 'react-router-dom';
import * as Yup from 'yup';
import login from '../actions/login';
import { ReactComponent as Logo } from '../assets/logo.svg';
import Permissions from '../consts/Permissions';
import isPermissionGranted from '../helpers/isPermissionGranted';
import useStore from '../store/store';

const LoginSchema = Yup.object().shape({
    iso2: Yup.string().required('Required'),
    phone: Yup.string()
        .required('Required')
        .matches(/^[\d\s]+X?$/, 'Invalid phone number!'),
    password: Yup.string().required('Required'),
});

export default function LoginFormPage() {
    const { countries, formError, loading, setFormError } = useStore(
        (state) => state
    );
    const accessToken = localStorage.getItem('accessToken');
    const iso2 = localStorage.getItem('iso2') || 'AE';
    const password = '';
    const phone = '';
    const classes = useStyles();

    const hasAdminPermission = isPermissionGranted(Permissions.admin_screen);

    // If we have successfully obtained the token, then redirect to main.
    if (accessToken) {
        if (hasAdminPermission) return <Navigate to="/admin" />;
        else return <Navigate to="/" />;
    }

    return (
        countries &&
        countries.length > 0 && (
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <div className={classes.paper}>
                    <Logo className={classes.logo} />
                    <Typography component="h1" variant="h5">
                        Sign in
                    </Typography>
                    <Formik
                        initialValues={{
                            iso2: iso2,
                            phone: phone,
                            password: password,
                        }}
                        onSubmit={login}
                        validationSchema={LoginSchema}
                        validateOnBlur
                        validateOnChange
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                        }) => (
                            <Form className={classes.form} noValidate>
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    margin="dense"
                                    error={!!(errors.iso2 && touched.iso2)}
                                >
                                    <InputLabel id="country-label">
                                        Country
                                    </InputLabel>
                                    <Select
                                        name="iso2"
                                        value={values.iso2}
                                        disabled={loading}
                                        error={errors.iso2 && touched.iso2}
                                        fullWidth
                                        id="iso2"
                                        labelId="country-label"
                                        onBlur={handleBlur}
                                        onChange={(value) => {
                                            formError && setFormError(null);
                                            handleChange(value);
                                        }}
                                        label="Country"
                                    >
                                        {countries.map((c) => (
                                            <MenuItem
                                                key={c.Iso2}
                                                value={c.Iso2}
                                            >
                                                {c.Name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.iso2 && touched.iso2 && (
                                        <FormHelperText>
                                            {errors.iso2}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                                <Field
                                    autoComplete="tel-national"
                                    component={TextField}
                                    value={values.phone}
                                    disabled={loading}
                                    error={errors.phone && touched.phone}
                                    fullWidth
                                    helperText={
                                        errors.phone && touched.phone
                                            ? errors.phone
                                            : ''
                                    }
                                    id="phone"
                                    label="Phone"
                                    margin="normal"
                                    name="phone"
                                    onBlur={handleBlur}
                                    onChange={(event) => {
                                        formError && setFormError(null);
                                        const numericValue =
                                            event.target.value.replace(
                                                /[^\d]/g,
                                                ''
                                            );
                                        handleChange({
                                            target: {
                                                name: 'phone',
                                                value: numericValue,
                                            },
                                        });
                                    }}
                                    size="small"
                                    type="tel"
                                    variant="outlined"
                                    inputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                    }}
                                />
                                <Field
                                    component={TextField}
                                    value={values.password}
                                    disabled={loading}
                                    error={errors.password && touched.password}
                                    fullWidth
                                    helperText={
                                        errors.password && touched.password
                                            ? errors.password
                                            : ''
                                    }
                                    id="password"
                                    label="Password"
                                    margin="normal"
                                    name="password"
                                    onBlur={handleBlur}
                                    onChange={(value) => {
                                        formError && setFormError(null);
                                        handleChange(value);
                                    }}
                                    size="small"
                                    type="password"
                                    variant="outlined"
                                />
                                {formError && (
                                    <Typography
                                        align="center"
                                        className={classes.errorMessage}
                                        color="error"
                                        variant="body1"
                                    >
                                        Incorrect login details!
                                    </Typography>
                                )}
                                <div className={classes.wrapper}>
                                    <Button
                                        color="primary"
                                        disabled={loading}
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                    >
                                        Sign In
                                    </Button>
                                    {loading && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </div>
                                <Link
                                    to="/register"
                                    className={classes.notification}
                                >
                                    <Typography
                                        align="center"
                                        variant="body1"
                                        className={classes.notification}
                                    >
                                        Don&apos;t have an account?{' '}
                                        <b>Sign up instead!</b>
                                    </Typography>
                                </Link>
                                <Link to="/forgot-password">
                                    <Typography
                                        align="center"
                                        variant="body1"
                                        className={classes.notification}
                                    >
                                        I forgot my password
                                    </Typography>
                                </Link>
                            </Form>
                        )}
                    </Formik>
                </div>
            </Container>
        )
    );
}

const useStyles = makeStyles((theme) => ({
    buttonProgress: {
        color: 'primary',
        left: '50%',
        marginLeft: -12,
        marginTop: -12,
        position: 'absolute',
        top: '50%',
    },
    errorMessage: {
        marginTop: theme.spacing(2),
    },
    form: {
        marginTop: theme.spacing(1),
        width: '100%', // Fix IE 11 issue.
    },
    logo: {
        marginBottom: theme.spacing(8),
        color: theme.palette.primary.main,
    },
    paper: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        marginTop: theme.spacing(8),
    },
    wrapper: {
        margin: theme.spacing(3, 0, 2),
        position: 'relative',
    },
    notification: {
        color: '#7C87FF',
    },
}));
